/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useMemo, useState } from "react";
import ReactFlow, { Edge, Handle, Node, Position } from "reactflow";
import { BracketSeries } from "@portal-frontend-ssr/blast-api";
import { FlowBracketMatch } from "./components/FlowBracketMatch";
import "reactflow/dist/style.css";
import { GameId } from "@portal-frontend-ssr/blast-api";
import { TeamSlot } from "~/routes/_cs.cs.bounty-simulator._index/components/TeamDropdown";

function MatchNode({
  data,
}: {
  data: {
    gameId: GameId;
    circuitName?: string;
    tournamentName?: string;
    matchName?: string;
    matchUuid?: string;
    teamAName?: string;
    teamBName?: string;
    match: BracketSeries;
    onTeamHover: (uuid?: string) => void;
    onTeamClick?: (match: BracketSeries, team: TeamSlot) => void;
    activeTeamId?: string;
    pickedResults?: Record<string, TeamSlot>;
  };
}) {
  return (
    <>
      <Handle type="target" position={Position.Left} className="invisible" />
      {/* custom match component showing team logos, names, scores */}
      <FlowBracketMatch
        gameId={data.gameId}
        tournamentName={data.tournamentName}
        match={data.match}
        onTeamHover={data.onTeamHover}
        onTeamClick={data.onTeamClick}
        activeTeamId={data.activeTeamId}
        pickedResults={data.pickedResults}
      />
      <Handle type="source" position={Position.Right} className="invisible" />
    </>
  );
}

export function FlowBracket({
  gameId,
  circuitName,
  tournamentName,
  matches,
  matchIndexPositions,
  onTeamClick,
  pickedResults,
}: {
  gameId: GameId;
  circuitName: string | undefined;
  tournamentName: string | undefined;
  matches: BracketSeries[];
  matchIndexPositions: {
    [key: number]: {
      node: {
        x: number;
        y: number;
      };
    };
  };
  onTeamClick?: (match: BracketSeries, team: TeamSlot) => void;
  pickedResults?: Record<string, TeamSlot>;
}) {
  const nodeTypes = useMemo(() => ({ match: MatchNode }), []);

  const [activeTeamId, setActiveTeamId] = useState<string>();

  const nodes = useMemo<Node<unknown, string | undefined>[]>(
    () =>
      matches?.map<Node<unknown, string | undefined>>((match) => ({
        id: match.uuid,
        type: "match",
        position: matchIndexPositions[match.index].node,
        style: {
          height: "103px", // Makes sure the connecting lines meet at the middle of the match container
        },
        data: {
          gameId,
          circuitName,
          tournamentName,
          match,
          activeTeamId,
          onTeamHover: setActiveTeamId,
          onTeamClick,
          pickedResults,
        },
      })),
    [matches, matchIndexPositions, gameId, circuitName, tournamentName, activeTeamId, onTeamClick, pickedResults],
  );

  const edges = useMemo<Edge<unknown>[]>(
    () =>
      matches
        ?.filter((match) => match.winnerGoesTo?.seriesUUID) // filter out grand final (no next match so so edge needed)
        .map<Edge<unknown>>((match) => {
          const animated =
            activeTeamId === undefined || !match.isCompleted
              ? false
              : match.teamA?.isEliminated
                ? match.teamB?.uuid === activeTeamId
                : match.teamA?.uuid === activeTeamId;

          return {
            id: `edge-${match.uuid}`,
            type: "smoothstep",
            source: match.uuid,
            target: match.winnerGoesTo!.seriesUUID,
            animated,
            zIndex: animated ? 10 : 0,
            style: {
              stroke: activeTeamId ? (animated ? "#fffe3e" : "#8D858C") : "#D2CED1",
            },
          };
        }),
    [matches, activeTeamId],
  );

  return (
    <button
      className="size-full"
      onMouseLeave={() => setActiveTeamId(undefined)}
      onClick={() => setActiveTeamId(undefined)}
    >
      <ReactFlow
        nodeTypes={nodeTypes}
        nodes={nodes}
        edges={edges}
        // prevent selecting nodes
        elementsSelectable={false}
        // prevent zooming
        zoomOnScroll={false}
        zoomOnDoubleClick={false}
        zoomOnPinch={false}
        // prevent panning & scrolling
        panOnDrag={false}
        preventScrolling={false}
        proOptions={{
          hideAttribution: true,
        }}
      />
    </button>
  );
}
